import axios, { type AxiosError } from 'axios';
import { getCSRFTokenCookie } from 'utils/cookies';
import { getSessionId, getHeaderCsrfToken } from 'utils/authStorage';
import { isElectron } from 'utils/environment';
import { notifySessionExpired } from 'utils/sessionExpiry';
import { APP_VERSION } from 'utils/appVersion';
import { endpoints } from './endpoints';

const baseURL = import.meta.env.VITE_API_BASE_URL as string;
const apiVersion = 'v1';

const fullBaseURL = `${baseURL}/${apiVersion}`;

const axiosInstance = axios.create({
  baseURL: fullBaseURL,
  withCredentials: !isElectron,
  headers: {
    'Content-Type': 'application/json',
    'X-App-Version': APP_VERSION,
  },
});

// Endpoints where a 401 is an expected outcome (bad credentials, or checking
// whether a session exists at all) rather than a sign that a live session died.
const SESSION_EXPIRY_EXCLUDED_ENDPOINTS: string[] = [
  endpoints.sessionLogin,
  endpoints.entraLogin,
  endpoints.sessionLogout,
  endpoints.csrf,
  endpoints.me,
];

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const requestPath = (error.config?.url ?? '').split('?')[0].replace(/\/+$/, '');
    const isExcluded = SESSION_EXPIRY_EXCLUDED_ENDPOINTS.some((endpoint) => {
      const normalizedEndpoint = endpoint.replace(/\/+$/, '');
      return requestPath === normalizedEndpoint || requestPath.endsWith(normalizedEndpoint);
    });
    if (error.response?.status === 401 && !isExcluded) {
      notifySessionExpired();
    }
    return Promise.reject(error);
  }
);

function buildUrl(base: string, path?: string | number | Array<string | number>): string {
  if (path === undefined || path === null || path === '') return base;
  const parts = Array.isArray(path) ? path : [path];
  const sanitized = parts
    .filter((p) => p !== undefined && p !== null && `${p}`.length > 0)
    .map((p) => encodeURIComponent(String(p).replace(/^\/+|\/+$/g, '')));
  return sanitized.length ? `${base}/${sanitized.join('/')}` : base;
}

export async function get(
  endpoint: keyof typeof endpoints,
  path?: string | number | Array<string | number>,
  params?: Record<string, unknown>
): Promise<unknown> {
  await setAuthHeader();
  const url = buildUrl(endpoints[endpoint], path);
  const response = await axiosInstance.get(url, { params });
  return response.data;
}

export async function post(
  endpoint: keyof typeof endpoints,
  data?: unknown,
  path?: string | number | Array<string | number>,
  params?: Record<string, unknown>
): Promise<unknown> {
  await setAuthHeader();
  const url = buildUrl(endpoints[endpoint], path);
  const response = await axiosInstance.post(url, data, { params });
  return response.data;
}

export async function patch(
  endpoint: keyof typeof endpoints,
  data?: unknown,
  path?: string | number | Array<string | number>,
  params?: Record<string, unknown>
): Promise<unknown> {
  await setAuthHeader();
  const url = buildUrl(endpoints[endpoint], path);
  const response = await axiosInstance.patch(url, data, { params });
  return response.data;
}

export async function put(
  endpoint: keyof typeof endpoints,
  data?: unknown,
  path?: string | number | Array<string | number>,
  params?: Record<string, unknown>
): Promise<unknown> {
  await setAuthHeader();
  const url = buildUrl(endpoints[endpoint], path);
  const response = await axiosInstance.put(url, data, { params });
  return response.data;
}

export async function deleteRequest(
  endpoint: keyof typeof endpoints,
  path?: string | number | Array<string | number>,
  params?: Record<string, unknown>
): Promise<unknown> {
  await setAuthHeader();
  const url = buildUrl(endpoints[endpoint], path);
  const response = await axiosInstance.delete(url, { params });
  return response.data;
}

// Authentication functions
export function clearAuth(): void {
  if (isElectron) {
    delete axiosInstance.defaults.headers.common['X-Session-Id'];
  }
  delete axiosInstance.defaults.headers.common['X-CSRFToken'];
}

export async function fetchCSRFCookie(): Promise<unknown> {
  return get('csrf');
}

export async function getCSRFCookie(): Promise<void> {
  const initialCsrfCookie = getCSRFTokenCookie();
  if (!initialCsrfCookie) {
    await fetchCSRFCookie();
    const csrfCookie = getCSRFTokenCookie();
    if (csrfCookie) {
      axiosInstance.defaults.headers.common['X-CSRFToken'] = csrfCookie;
    }
  }
}

async function setAuthHeader() {
  if (isElectron) {
    const sessionId = await getSessionId();
    if (sessionId) {
      axiosInstance.defaults.headers.common['X-Session-Id'] = sessionId;
    } else {
      delete axiosInstance.defaults.headers.common['X-Session-Id'];
    }

    const csrfToken = await getHeaderCsrfToken();
    if (csrfToken) {
      axiosInstance.defaults.headers.common['X-CSRFToken'] = csrfToken;
    } else {
      delete axiosInstance.defaults.headers.common['X-CSRFToken'];
    }
  } else {
    const csrfCookie = getCSRFTokenCookie();
    if (csrfCookie) {
      axiosInstance.defaults.headers.common['X-CSRFToken'] = csrfCookie;
    } else {
      console.warn('CSRF token cookie not found');
    }
  }
}
